import React, { useEffect, useState, useImperativeHandle, forwardRef, useCallback } from "react";
import { api, fmtDate } from "@/lib/api";
import { ClipboardList, CheckCircle2, XCircle, Clock } from "lucide-react";
import { logError } from "@/lib/log";

/**
 * Customer's recent delivery-request history.
 * Exposes a `reload()` method via ref so parent can refresh after submitting.
 */
const PortalRequestsList = forwardRef(function PortalRequestsList(_props, ref) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/portal/tickets");
      // Only show portal-initiated requests + recent non-portal entries
      setItems((r.data || []).filter((t) => t.customer_requested || t.status === "pending"));
    } catch (e) { logError("Portal requests load failed:", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useImperativeHandle(ref, () => ({ reload: load }));

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <section className="border border-border" data-testid="portal-requests-list">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-primary" />
          <h2 className="font-display text-xl font-bold tracking-tight">Your delivery requests</h2>
        </div>
      </div>
      <div className="divide-y divide-border">
        {items.map((t) => {
          const totalGal = (t.pool_deliveries || []).reduce((s, d) => s + Number(d.gallons || 0), 0);
          const cfg = {
            pending:  { Icon: Clock, label: "Pending review", cls: "text-yellow-600 bg-yellow-50" },
            open:     { Icon: CheckCircle2, label: "Approved", cls: "text-primary bg-primary/10" },
            closed:   { Icon: CheckCircle2, label: "Delivered", cls: "text-success bg-success/10" },
            declined: { Icon: XCircle, label: "Declined", cls: "text-destructive bg-destructive/10" },
          }[t.status] || { Icon: Clock, label: t.status, cls: "text-muted-foreground bg-secondary" };
          const Icon = cfg.Icon;
          return (
            <div key={t.id} className="px-6 py-4 flex items-center justify-between gap-4" data-testid={`portal-req-row-${t.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${cfg.cls}`}>
                    <Icon className="w-3 h-3" /> {cfg.label}
                  </span>
                  <span className="text-sm font-mono text-muted-foreground">{fmtDate(t.delivery_date)}</span>
                </div>
                {t.notes && <div className="text-xs text-muted-foreground mt-1 font-mono italic">&ldquo;{t.notes}&rdquo;</div>}
                {(t.items || []).length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1" data-testid={`portal-req-items-${t.id}`}>
                    {(t.items || []).map((i) => `${Number(i.quantity)}× ${i.name || "Product"}`).join(", ")}
                  </div>
                )}
                {t.declined_reason && <div className="text-xs text-destructive mt-1">Reason: {t.declined_reason}</div>}
              </div>
              <div className="mono-num text-sm text-muted-foreground">
                {totalGal > 0 ? `${totalGal} gal` : ""}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
});

export default PortalRequestsList;
